// @flow

import type { UiRoleId } from '../../main/types/accessControlTypes';

export const ACCESS_LEVEL_DOCS =
  'https://docs.branch.io/pages/dashboard/access-level';

// If access_role_id is not 1-4, it is a custom role
export const ROLE_IDS: { [roleType: string]: UiRoleId } = {
  ADMIN: 1,
  TEAM_MEMBER: 2,
  FULL_READ: 3,
  LIMITED_READ: 4,
  // Setting a value for 'Custom' role only for the UI - it will get its own
  // unique role id when saved in the database as a custom role (not always 5).
  CUSTOM: 5,
  // No access will give a user an access role with all access resource pemissions set to 0
  NO_ACCESS: 200
};

export const AGENCY_ROLE_IDS: { [roleType: string]: UiRoleId } = {
  ADMIN: 101,
  TEAM_MEMBER: 102
};

export const GREATEST_PREDEFINED_ROLE_ID = Object.keys(ROLE_IDS).reduce(
  (highest, role) => {
    const roleId = ROLE_IDS[role];
    return roleId !== ROLE_IDS.CUSTOM && roleId > highest ? roleId : highest;
  },
  0
);

export const selectableRoleOptions = (
  includeNoAccess: ?boolean
): Array<any> => {
  const roles: Array<any> = [
    {
      name: 'admin',
      label: 'Admin',
      value: ROLE_IDS.ADMIN,
      description: 'Edit access to all settings and export access to all data'
    },
    {
      name: 'team_member',
      label: 'Team Member',
      value: ROLE_IDS.TEAM_MEMBER,
      description:
        'Edit access to channels and links, read-only access to app settings, and access to aggregate data'
    },
    {
      name: 'full_read',
      label: 'Full Read',
      value: ROLE_IDS.FULL_READ,
      description:
        'Read-only access to all settings, and access to aggregate data'
    },
    {
      name: 'limited_read',
      label: 'Limited Read',
      value: ROLE_IDS.LIMITED_READ,
      description: 'Access to aggregate data'
    },
    {
      label: 'Custom',
      value: ROLE_IDS.CUSTOM,
      description: 'Customize settings and data access'
    }
  ];
  const noAccessRole = [
    {
      label: 'No Access',
      value: ROLE_IDS.NO_ACCESS,
      description: 'No dashboard access'
    }
  ];
  return includeNoAccess ? roles.concat(noAccessRole) : roles;
};

export const SELECTABLE_AGENCY_ROLE_OPTIONS = [
  {
    name: 'agency_admin',
    label: 'Agency Admin',
    value: AGENCY_ROLE_IDS.ADMIN,
    description:
      'Full access to Agency settings tab, including the ability to add, edit, and remove team members.'
  },
  {
    name: 'agency_team_member',
    label: 'Agency Team Member',
    value: AGENCY_ROLE_IDS.TEAM_MEMBER,
    description: 'Read-only access to Agency settings tab.'
  }
];

/**
 * These values have been hard coded for defaultProps in
 * common/components/AccessControl
 * Make sure to change those if updating READ_PERMISSION or WRITE_PERMISSION
 */
export const PERMISSION_OVERRIDE = 0;
export const READ_PERMISSION = 1;
export const WRITE_PERMISSION = 2;

export const ACCESS_CONTROL_PROPS = [
  'accessResources',
  'permission',
  'accessDeniedPropName'
];

export const ROLE_IDS_TO_HUMAN_READABLE = [
  ...selectableRoleOptions(true),
  ...SELECTABLE_AGENCY_ROLE_OPTIONS
].reduce((humanReadableRoles, role) => {
  humanReadableRoles[role.value] = role.label;
  return humanReadableRoles;
}, {});

// accessResource options
/**
 * These values have been hard coded for defaultProps in
 * common/components/AccessControl
 * Make sure to change those if updating these resource constants
 */
export const SENSITIVE_DATA = 'sensitive_data';
export const AGGREGATE_DATA = 'aggregate_data';
export const APP_LEVEL_SETTINGS = 'app_level_settings';
export const TEAM_ACCESS = 'team_access';
export const CHANNEL_LEVEL_SETTINGS = 'channel_level_settings';
export const LINK_LEVEL_SETTINGS = 'link_level_settings';
export const EXPORT_ACCESS = 'export';
export const LOCATION = 'location';
export const AD_NETWORKS = 'ad_networks';
export const REVENUE_DATA = 'revenue_data';
export const AGENCY_TAGGED_DATA = 'agency_tagged_data';
export const FRAUD_SETTINGS_AND_DATA = 'fraud_settings_and_data';
export const AUDIENCE_BUILDER = 'audience_builder';

export const UI_PERMISSIONS_HEADING = 'Permissions';

// accessResource utils
export const UI_RESOURCE_OPTIONS = [
  {
    id: LINK_LEVEL_SETTINGS,
    label: 'Link-Level Settings',
    tooltip:
      'Settings or features that can impact functionality for single links.',
    read_possible: true,
    write_possible: true
  },
  {
    id: CHANNEL_LEVEL_SETTINGS,
    label: 'Channel-Level Settings',
    tooltip:
      'Settings or features that can impact functionality across a marketing channel. This includes ad partner management and postback configuration, which can be used to send log-level data to third parties (e.g. ad partners).',
    read_possible: true,
    write_possible: true
  },
  {
    id: APP_LEVEL_SETTINGS,
    label: 'App-Level Settings',
    tooltip: 'Settings or features that can impact functionality app-wide.',
    read_possible: true,
    write_possible: true
  },
  {
    id: AGGREGATE_DATA,
    label: 'Aggregate Data',
    tooltip: 'Summary data that contains no granular information.',
    read_possible: true,
    write_possible: false
  },
  {
    id: SENSITIVE_DATA,
    label: 'Sensitive Data',
    tooltip:
      'Data that can contain user-identifying, payment-related, or secret information.',
    read_possible: true,
    /* In order to not confuse the user about the difference of app-level write
      and sensitive data write, we only want to show the read checkbox for
      sensitive data. However when the user saves we will save as write (in
      all cases, not just when app-level permissions given).We decided if a
      user can see sensitive data they should be able to modify that sensitive data
    */
    write_possible: false
  },
  {
    id: FRAUD_SETTINGS_AND_DATA,
    label: 'Fraud Settings & Data',
    tooltip: 'Settings or data associated with fraud detection and prevention.',
    read_possible: true,
    write_possible: true
  },
  {
    id: AUDIENCE_BUILDER,
    label: 'Engagement Builder',
    tooltip:
      'Settings or data associated with engagement group creation and export.',
    read_possible: true,
    write_possible: true
  }
];

const UI_RESOURCES_TO_HUMAN_READABLE = UI_RESOURCE_OPTIONS.reduce(
  (humanReadableResources, resource) => {
    humanReadableResources[resource.id] = resource.label.toLowerCase();
    return humanReadableResources;
  },
  {}
);
// Some resources we don't want in the permissions table in the UI,
// but we want to ensure it still has a translation outside the table:
export const ACCESS_RESOURCES_TO_HUMAN_READABLE = {
  ...UI_RESOURCES_TO_HUMAN_READABLE,
  ...{ [EXPORT_ACCESS]: 'export permissions' },
  ...{ [TEAM_ACCESS]: 'app-level settings and sensitive data' },
  ...{ [LOCATION]: 'all locations' },
  ...{ [AD_NETWORKS]: 'all ad networks' },
  ...{ [REVENUE_DATA]: 'revenue data' },
  ...{ [AGENCY_TAGGED_DATA]: 'non agency tagged data' }
};

export const UI_DATA_RESTRICTIONS_HEADING = 'Data Filters';

export const UI_DATA_RESTRICTION_OPTIONS = [
  {
    id: AGENCY_TAGGED_DATA,
    label: 'Only show agency-tagged data',
    tooltip:
      'When toggled on, agency users can only view events tagged with their Agency ID.'
  },
  {
    id: REVENUE_DATA,
    label: 'Restrict access to revenue data',
    tooltip: 'When toggled on, agency users cannot view revenue data.'
  },
  {
    id: AD_NETWORKS,
    label: 'Only show data from specific ad networks',
    tooltip:
      'When toggled on, agency users can only view events from a specific list of ad networks.'
  },
  {
    id: LOCATION,
    label: 'Only show data from specific locations',
    tooltip:
      'When toggled on, agency users can only view events that have taken place in a specific list of countries.'
  }
];

export const ALL_AGENCY_DATA_FILTERS_COPY = ', with no data filters';

export const AGENCY_DATA_FILTERS = [
  AGENCY_TAGGED_DATA,
  REVENUE_DATA,
  AD_NETWORKS,
  LOCATION
];
